import React from 'react';
import styled from 'styled-components';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  position: relative;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-right: 32px;
`;

const IconWrapper = styled.div`
  margin-right: 10px;
  flex-shrink: 0;
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const Message = styled.p`
  margin: 0 0 20px 0;
  color: #6b7280;
  line-height: 1.6;
  font-size: 14px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  &.primary {
    background: #6366f1;
    color: white;
    
    &:hover {
      background: #5856eb;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(99, 102, 241, 0.3);
    }
    
    &:active {
      transform: translateY(0);
    }
  }
  
  &.secondary {
    background: #f3f4f6;
    color: #374151;
    
    &:hover {
      background: #e5e7eb;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  color: #6b7280;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const AlertModal = ({ 
  isOpen, 
  onClose, 
  title, 
  message, 
  type = 'info',
  onConfirm,
  confirmText = '确定',
  showCancel = false,
  cancelText = '取消'
}) => {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle color="#10b981" />;
      case 'error':
        return <AlertCircle color="#ef4444" />;
      case 'warning':
        return <AlertCircle color="#f59e0b" />;
      default:
        return <Info color="#3b82f6" />;
    }
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X />
        </CloseButton>
        
        <Header>
          <IconWrapper>
            {getIcon()}
          </IconWrapper>
          <Title>{title}</Title>
        </Header>
        
        <Message>{message}</Message>
        
        <ButtonGroup>
          {showCancel && (
            <Button className="secondary" onClick={onClose}>
              {cancelText}
            </Button>
          )}
          <Button className="primary" onClick={handleConfirm}>
            {confirmText}
          </Button>
        </ButtonGroup>
      </Modal>
    </Overlay>
  );
};

export default AlertModal;
